import "./service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

const offerings = [
  {
    icon: "monitor",
    title: "Full-Stack Web Development",
    desc: "Complete web application development using modern frontend and backend technologies with scalable architecture and clean user experiences.",
  },
  {
    icon: "dashboard",
    title: "Custom Dashboards & Admin Panels",
    desc: "Build powerful management systems, analytics dashboards, internal tools, and operational platforms with real-time monitoring and intelligent workflows.",
  },
  {
    icon: "cart",
    title: "E-Commerce Platform Development",
    desc: "Develop scalable online stores and commerce ecosystems with secure payment systems, inventory management, customer analytics, and optimized user experiences.",
  },
  {
    icon: "layers",
    title: "SaaS Product Development",
    desc: "Build scalable multi-tenant SaaS applications with subscription systems, cloud deployment, onboarding flows, and enterprise-ready infrastructure.",
  },
  {
    icon: "plug",
    title: "API Development & System Integration",
    desc: "Design secure, high-performance APIs and integrate third-party services, enterprise systems, payment providers, and cloud platforms.",
  },
  {
    icon: "clock",
    title: "Real-Time Applications",
    desc: "Develop high-performance real-time applications including live dashboards, chat systems, notifications, streaming platforms, and collaborative tools.",
  },
];

const techGroups = [
  { label: "Frontend Technologies", items: ["React.js", "Next.js", "TypeScript", "Tailwind CSS", "Redux Toolkit", "React Query"] },
  { label: "Backend Technologies", items: ["Node.js", "Express.js", "NestJS", "FastAPI", "GraphQL", "REST APIs"] },
  { label: "Database Technologies", items: ["MongoDB", "PostgreSQL", "MySQL", "Redis", "Firebase", "Supabase"] },
  { label: "Cloud & DevOps", items: ["AWS", "Docker", "Kubernetes", "CI/CD Pipelines", "Nginx", "Vercel"] },
];

const keyFeatures = [
  {
    icon: "responsive",
    title: "Responsive UI/UX Systems",
    desc: "Modern user interfaces designed for smooth experiences across desktop, tablet, and mobile devices with accessibility-focused interactions.",
  },
  {
    icon: "bolt",
    title: "High Performance & Optimization",
    desc: "Optimized applications engineered for fast loading, efficient rendering, scalable infrastructure, and excellent runtime performance.",
  },
  {
    icon: "trending",
    title: "SEO-Friendly Architecture",
    desc: "Search engine optimized web platforms with semantic structure, structured metadata, and technical SEO best practices.",
  },
  {
    icon: "shield",
    title: "Secure Authentication & Authorization",
    desc: "Implement secure authentication systems, role-based access control, encryption standards, and enterprise-grade application security.",
  },
];

export default function WebApplication() {
  const [offeringsRef, offeringsVisible] = useReveal();
  const [featuresRef, featuresVisible] = useReveal();

  return (
    <>
      <SEO
        title="Web Application Development: React & Node.js | Best Code Technology"
        description="Custom web application development using React, Node.js and modern scalable technologies."
        keywords="web application development, React development, Node.js services"
        url="https://bestcodetechnology.in/services/web-applications"
      />

      <div className="svc-page">
        {/* ── Hero ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Build Modern, Scalable &amp; <span>High-Performance Web Applications</span>
                </h1>
                <p className="svc-hero__desc">
                  We design and develop fast, secure, and scalable web platforms
                  for startups, enterprises, and digital businesses. From SaaS
                  products and admin dashboards to enterprise systems and
                  real-time applications, we build production-ready web solutions
                  engineered for long-term growth.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── What We Offer ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <h2 className="svc-section-title">
                  Web Engineering
                </h2>
                <p className="svc-section-sub">
                  We provide end-to-end web development services using modern
                  frontend frameworks, scalable backend architectures, cloud
                  infrastructure, and enterprise-grade engineering practices.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={offeringsRef}>
              {offerings.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${offeringsVisible ? "" : "is-hidden"}`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tech Stack ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row align-items-start g-5">
              <div className="col-12 col-lg-4 svc-tech__col-left pe-lg-5">
                <h2 className="svc-section-title">Our Web Technology Stack</h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Modern technologies selected for scalability, maintainability,
                  developer productivity, and enterprise-grade performance.
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="svc-tech__groups">
                  {techGroups.map((group) => (
                    <div key={group.label} className="svc-tech__group">
                      <span className="svc-tech__group-label">
                        {group.label}
                      </span>
                      <div className="svc-tech__badges">
                        {group.items.map((tech) => (
                          <span key={tech} className="svc-tech__badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Key Features ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <h2 className="svc-section-title">
                  Built Right From the Start
                </h2>
                <p className="svc-section-sub">
                  Every web application we develop is engineered for
                  scalability, performance, security, maintainability, and
                  exceptional user experience.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={featuresRef}>
              {keyFeatures.map((feat, i) => (
                <div
                  key={feat.title}
                  className={`col-12 col-lg-6 reveal ${
                    featuresVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__top">
                      <div className="svc-usecase-card__icon">
                        <Icon name={feat.icon} size={22} />
                      </div>
                    </div>
                    <h3 className="svc-usecase-card__title">{feat.title}</h3>
                    <p className="svc-usecase-card__desc">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
